"use client";

import type { ChangeEvent } from "react";
import type { MotionValue } from "motion/react";
import type { Segment, TranscriptionResponse } from "@ui/components/demos/STT/types";

import { useRef } from "react";
import { useTranslations } from "next-intl";

import { Button } from "@ui/components/core/Button";
import { Skeleton } from "@ui/components/core/Skeleton";
import { ListBox } from "@ui/components/core/ListBox";
import { LimitReachedOverlay } from "@ui/components/demos/LimitReachedOverlay";
import { STTHeroTranscript } from "./STTHeroTranscript";
import { appUrls } from "@/constants";
import IconArrowInbox from "@ui/central-icons/icons/IconArrowInbox";
import IconPlayFill from "@ui/central-icons/icons/IconPlayFill";
import IconPauseFill from "@ui/central-icons/icons/IconPauseFill";
import { STTUploadingView } from "./STTUploadingView";
import { DottedBorder } from "@ui/components/core/GradientBorder";
import type { Media } from "@mw/shared/payload-types";

import type { STTHeroDisplayMode } from "./useSTTHeroPlayback";
import { STTFloatingCardHeader } from "./STTFloatingCardHeader";
import { useSTTFloatingCardHeader } from "./useSTTFloatingCardHeader";
import { useSTTFloatingCardDownloads } from "./useSTTFloatingCardDownloads";

export interface STTFloatingCardProps {
  displayMode?: STTHeroDisplayMode;
  sampleThumbnail?: Media | null;
  uploadedMediaKind?: "audio" | "video" | null;
  sampleName: string;
  segments: Segment[];
  isLoading: boolean;
  isPlaying: boolean;
  isUploading?: boolean;
  uploadFileName?: string | null;
  uploadedTranscription?: TranscriptionResponse | null;
  uploadedThumbnailUrl?: string | null;
  currentTime: MotionValue<number>;
  onTogglePlay: () => void;
  onWordClick: (time: number) => void;
  onFileChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onDownload?: () => void;
  onTranscriptionDownloaded?: (download_format: string) => void;
  showLimitReachedOverlay?: boolean;
}

export function STTFloatingCard({
  displayMode = "default",
  sampleThumbnail = null,
  uploadedMediaKind = null,
  sampleName,
  segments,
  isLoading,
  isPlaying,
  isUploading = false,
  uploadFileName = null,
  uploadedTranscription = null,
  uploadedThumbnailUrl = null,
  currentTime,
  onTogglePlay,
  onWordClick,
  onFileChange,
  onDownload,
  onTranscriptionDownloaded,
  showLimitReachedOverlay = false,
}: STTFloatingCardProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const downloadButtonRef = useRef<HTMLButtonElement | null>(null);
  const t = useTranslations("ClientKeys.STTHero");
  const tSpeechToText = useTranslations("ClientKeys.SpeechToText");

  const headerModel = useSTTFloatingCardHeader({
    displayMode,
    sampleThumbnail,
    uploadedMediaKind,
    sampleName,
    segments,
    uploadFileName,
    uploadedThumbnailUrl,
  });
  const { formatItems, hasFormats, isFormatDropdownOpen, setIsFormatDropdownOpen, handleDownloadClick } =
    useSTTFloatingCardDownloads({
      uploadedTranscription,
      uploadFileName,
      sampleName,
      onDownload,
      onTranscriptionDownloaded,
    });

  if (isUploading) {
    return (
      <div
        className='tw-mx-auto tw-w-[375px] tw-max-w-full tw-h-[min(530px,calc(100svh_-_210px))] tw-min-h-0 tw-bg-white tw-rounded-3xl tw-flex tw-flex-col tw-gap-6 tw-p-6 tw-overflow-hidden tw-relative'
        style={{ boxShadow: "0 4px 24px rgb(0 0 0 / 0.12), 0 1px 2px rgb(0 0 0 / 0.06)" }}
      >
        <STTUploadingView uploadFileName={uploadFileName} />
      </div>
    );
  }

  return (
    <div
      className='tw-mx-auto tw-w-[375px] tw-max-w-full tw-h-[min(530px,calc(100svh_-_210px))] tw-min-h-0 tw-bg-white tw-rounded-3xl tw-flex tw-flex-col tw-gap-6 tw-p-6 tw-overflow-hidden tw-relative'
      style={{ boxShadow: "0 4px 24px rgb(0 0 0 / 0.12), 0 1px 2px rgb(0 0 0 / 0.06)" }}
    >
      {showLimitReachedOverlay && (
        <div className='tw-absolute tw-inset-0 tw-z-30 tw-flex tw-items-center tw-justify-center tw-rounded-3xl tw-bg-cream'>
          <LimitReachedOverlay
            rounded
            variant='redesigned'
            ctaLink={appUrls.speechToText}
            description={tSpeechToText("widget.quota_exceeded_description")}
            title={tSpeechToText("widget.quota_exceeded_title")}
          />
        </div>
      )}
      <STTFloatingCardHeader model={headerModel} />

      {headerModel.showDottedDivider ? (
        <div className='tw-relative tw-h-0.5 tw-shrink-0'>
          <DottedBorder side='top' color='rgba(0,0,0,0.1)' />
        </div>
      ) : null}

      {/* Transcript body */}
      <div className='tw-flex-1 tw-min-h-0 tw-relative tw-overflow-hidden tw-flex tw-flex-col'>
        {isLoading ? (
          <div className='tw-flex tw-flex-col tw-gap-3 tw-opacity-40'>
            <Skeleton className='tw-h-4 tw-w-3/4' />
            <Skeleton className='tw-h-4 tw-w-full' />
            <Skeleton className='tw-h-4 tw-w-full' />
            <Skeleton className='tw-h-4 tw-w-5/6' />
            <Skeleton className='tw-h-4 tw-w-full' />
            <Skeleton className='tw-h-4 tw-w-full' />
            <Skeleton className='tw-h-4 tw-w-4/5' />
          </div>
        ) : (
          <STTHeroTranscript
            currentTime={currentTime}
            segments={segments}
            onWordClick={onWordClick}
            isPlaying={isPlaying}
            bottomOffset={98}
          />
        )}
      </div>

      {/* Footer: absolute overlay with gradient fade */}
      <div className='tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-h-[98px] tw-bg-gradient-to-b tw-from-transparent tw-to-white tw-flex tw-items-end tw-justify-between tw-pt-8 tw-pb-3 tw-px-3 tw-pointer-events-none'>
        {/* Mobile: icon-only pill button */}
        <Button
          className='tw-pointer-events-auto sm:tw-hidden'
          variant='primary'
          size='pill'
          aria-label={isPlaying ? t("pause") : t("play")}
          onClick={onTogglePlay}
        >
          {isPlaying ? <IconPauseFill className='tw-w-4 tw-h-4' /> : <IconPlayFill className='tw-w-4 tw-h-4' />}
        </Button>

        {/* Desktop: text button */}
        <Button
          className='tw-pointer-events-auto tw-hidden sm:tw-block'
          variant='primary'
          size='md'
          onClick={onTogglePlay}
        >
          {isPlaying ? t("pause") : t("play")}
        </Button>

        <div className='tw-flex tw-items-center tw-gap-2'>
          <Button
            ref={downloadButtonRef}
            className='tw-pointer-events-auto'
            variant='elevated'
            size='pill'
            aria-label={t("download")}
            aria-expanded={hasFormats ? isFormatDropdownOpen : undefined}
            onClick={handleDownloadClick}
          >
            <IconArrowInbox className='tw-w-4 tw-h-4' />
          </Button>

          {hasFormats && (
            <ListBox
              items={formatItems}
              triggerRef={downloadButtonRef}
              isOpen={isFormatDropdownOpen}
              onOpenChange={setIsFormatDropdownOpen}
              asMenu
              placement='top end'
              aria-label={t("download_format")}
            />
          )}

          <Button
            className='tw-pointer-events-auto'
            variant='elevated'
            size='md'
            onClick={() => fileInputRef.current?.click()}
          >
            {t("upload_file")}
          </Button>
        </div>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type='file'
        accept='audio/*,video/*'
        className='tw-hidden'
        onChange={onFileChange}
        aria-hidden='true'
      />
    </div>
  );
}

export default STTFloatingCard;
